/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ProgressBar } from "primereact/progressbar";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import EnviarArchivo from "./EnviarArchivo";
import RequiereAtencion from "../inicio/RequiereAtencion";
import { Column } from "primereact/column";
import { accessAPI, ArchivoPreview, Boton, downloadFile, TablaFiltrable, Titulo, useOrganismo, tituloBase } from "../../shared";
import { avanceProceso, conMayuscula } from "../../portal/utilidades";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";

// Pantalla de una actividad abierta del cliente (reemplaza al detalle del
// proceso): su avance por pasos y los archivos que puede ver, con el envío de
// archivos a la actividad. Una actividad cerrada no se muestra.
export default function DetalleActividad() {
  const organismo = useOrganismo();
  const { actividadId } = useParams();
  const [actividad, setActividad] = useState(null);
  // Título de la pestaña, como en el admin: el de este registro cuando carga
  useEffect(() => {
    if (actividad) document.title = actividad.tipoNombre || actividad.actividadtipo?.nombre || "Actividad";
  }, [actividad]);
  useEffect(() => () => (document.title = tituloBase()), []);
  const [error, setError] = useState(false);
  const [descargandoId, setDescargandoId] = useState(null);
  const [enviarVisible, setEnviarVisible] = useState(false);
  const toast = useRef(null);

  const cargarActividad = () =>
    accessAPI("GET", `cliente/proceso/actividad/${actividadId}`, null, setActividad, () => setError(true));

  useEffect(() => {
    setActividad(null);
    setError(false);
    cargarActividad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actividadId]);

  if (error) {
    return (
      <>
        <Titulo urlVolver="/procesos">
          Actividad
        </Titulo>
        <Card css={{ marginBottom: "1em" }}>
          <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
            <i className="pi pi-exclamation-circle" css={{ fontSize: "1.5rem" }} />
            <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>No se encontró la actividad</div>
            <p css={{ margin: "0.25em 0 0" }}>Puede que no exista, que ya esté cerrada o que no pertenezca a tu empresa.</p>
          </div>
        </Card>
      </>
    );
  }

  if (!actividad) {
    return (
      <>
        <Skeleton height="4rem" borderRadius="10px" css={{ marginBottom: "1rem" }} />
        <Skeleton height="8rem" borderRadius="10px" css={{ marginBottom: "1rem" }} />
        <Skeleton height="8rem" borderRadius="10px" />
      </>
    );
  }

  const { proceso } = actividad;
  // El avance de una sola actividad: mismo cálculo que el del proceso
  const avance = avanceProceso({ actividad: [actividad] });
  const a = avance.actividades[0];
  const certificado = proceso.certificado?.[0];

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <EnviarArchivo
        visible={enviarVisible}
        onHide={() => setEnviarVisible(false)}
        actividadId={actividad.id}
        onEnviado={cargarActividad}
        toast={toast}
      />
      <Titulo
        urlVolver="/procesos"
        subtitulo={[`Proceso ${proceso.nombre || ""}`, proceso.nombrefantasia].filter(Boolean).join(" · ")}
        botones={
          certificado && (
            <Boton transparente uri={`/certificado/${certificado.id}?volver=/actividades/${actividad.id}`}>
              Ver certificado {certificado.numero}
            </Boton>
          )
        }
      >
        {a.nombre}
      </Titulo>

      {/* Grilla con 1em entre cards, también cuando aparece la de hallazgos */}
      <div css={{ display: "grid", gap: "1em", marginBottom: "1em" }}>
        {/* Hallazgos de esta actividad que esperan respuesta (si hay) */}
        <RequiereAtencion soloHallazgos actividadId={actividad.id} urlVolver={`/actividades/${actividad.id}`} />

        <Card>
          <Titulo subtitulo={
            avance.total && a.pasos.length
              ? `${avance.finalizados} de ${avance.total} pasos completados`
              : `${conMayuscula(organismo)} todavía no inició esta actividad`
          } botones={
            avance.etapaActual && avance.diasEnEtapa != null && (
              <Tag
                // Días en el paso: verde hasta 3, ámbar hasta 5, rojo después
                severity={avance.diasEnEtapa <= 3 ? "success" : avance.diasEnEtapa <= 5 ? "warning" : "danger"}
                icon="pi pi-clock"
              >
                {avance.diasEnEtapa} {avance.diasEnEtapa === 1 ? "día" : "días"} en el paso actual
              </Tag>
            )
          }>
            Avance de la actividad
          </Titulo>
          {a.pasos.length > 0 && (
            <>
              <div css={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
                <ProgressBar
                  value={avance.porcentaje}
                  showValue={false}
                  css={{ flex: 1, height: "0.5rem", "& .p-progressbar-value": { background: "var(--principal)" } }}
                />
                <span css={{ fontSize: "0.85em", color: "var(--grisTextos)", whiteSpace: "nowrap" }}>
                  {a.pasoActual ? `Paso ${a.numeroPaso} de ${a.pasos.length}` : "Por comenzar"}
                </span>
              </div>
              {/* Pasos en el orden del flujo: hechos (verde), el actual (color del
                  organismo) y los que faltan */}
              <div
                css={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "0.5rem"
                }}
              >
                {a.pasos.map((p, i) => (
                  <PasoActividad key={p.id} paso={p} numero={i + 1} />
                ))}
              </div>
            </>
          )}
        </Card>

        <Card>
          <Titulo botones={
            <Boton transparente callback={() => setEnviarVisible(true)}>
              Subir archivo
            </Boton>
          }>
            Archivos
          </Titulo>
          {actividad.archivos.length === 0 ? (
            <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
              <i className="pi pi-folder" css={{ fontSize: "1.5rem" }} />
              <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>No hay archivos disponibles</div>
            </div>
          ) : (
            <TablaFiltrable conFiltros={false}
              // La key fuerza a redibujar el loader del botón de descarga
              key={`archivos-${descargandoId}`}
              value={actividad.archivos}
              dataKey="id"
              size="small"
              className="p-tabla"
              nombreArchivo="archivos_actividad"
            >
              <Column
                header="Archivo"
                body={(archivo) => (
                  <ArchivoPreview
                    nombre={archivo.nombredescarga}
                    hash={archivo.hash}
                    subtitulo={
                      archivo.subidoPorCliente &&
                      `Enviado por tu empresa${archivo.nota ? ` · ${archivo.nota}` : ""}`
                    }
                  />
                )}
                exportValue={(archivo) => archivo.nombredescarga}
              />
              <Column
                body={(archivo) => (
                  <Boton
                    icono
                    chico
                    loader={descargandoId === archivo.id}
                    callback={() => {
                      setDescargandoId(archivo.id);
                      downloadFile(
                        `cliente/proceso/${proceso.id}/archivo/${archivo.id}`,
                        archivo.nombredescarga,
                        () => setDescargandoId(null),
                        () => setDescargandoId(null)
                      );
                    }}
                  >
                    <i className="pi pi-download" />
                  </Boton>
                )}
                style={{ width: "4rem", textAlign: "center" }}
              />
            </TablaFiltrable>
          )}
        </Card>
      </div>
    </>
  );
}

// Estilo de cada paso según su estado (antes .p-etapa en portal.css)
const ESTILO_PASO = {
  hecho: { borde: "var(--verde)", fondo: "#f8f9fa", icono: "pi pi-check-circle", color: "var(--verde)", texto: "Completado" },
  actual: { borde: "var(--principal)", fondo: "var(--principalLight)", icono: "pi pi-arrow-circle-right", color: "var(--principal)", texto: "En curso" },
  pendiente: { borde: "#e0e0e0", fondo: "#f8f9fa", icono: "pi pi-circle", color: "var(--grisTextos)", texto: "Pendiente" }
};

// Un paso de la actividad: número y nombre, y su estado con un ícono
function PasoActividad({ paso, numero }) {
  const estilo = ESTILO_PASO[paso.estado] || ESTILO_PASO.pendiente;
  return (
    <div
      css={{
        borderRadius: 8,
        background: estilo.fondo,
        borderLeft: `3px solid ${estilo.borde}`,
        padding: "0.6rem 0.75rem",
        fontSize: "0.85rem",
        lineHeight: 1.35,
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem"
      }}
    >
      <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)" }}>
        {numero}. {paso.nombre}
      </div>
      <div css={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.9em", color: estilo.color }}>
        <i className={estilo.icono} css={{ fontSize: "0.9em" }} />
        {estilo.texto}
      </div>
    </div>
  );
}
